import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer } from 'recharts';
import { PieChart, Pie, Cell } from 'recharts';
import { Users } from 'lucide-react';
import { UserCheck } from 'lucide-react';
import { Building2 } from 'lucide-react';
import { DollarSign } from 'lucide-react';
import FeePending from '@/components/Pendingfee';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import { useGetAllQuery } from '../store/studentApi';
import { useGetBatchQuery } from '../store/batchApi';
import { useGetAllCourseQuery } from '../store/courseApi';
import { useGetAllEnquiryQuery } from '../store/enquiryApi';
import { useGetAllFeeQuery } from '../store/feeApi';
import { Link } from 'react-router-dom';
import AttendanceBar from '@/components/AttendanceBar';

const HrDashboard = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const { data: students } = useGetAllQuery(user?.token);
  const { data: batches } = useGetBatchQuery();
  const { data: courses } = useGetAllCourseQuery();
  const { data: enquiries } = useGetAllEnquiryQuery(user?.token);

  // const userName = user?.name

  const lengthOfBatches = batches?.length || 0;
  const lengthOfCourses = courses?.length || 0;
  const lengthOfEnquiries = enquiries?.length || 0;

  const { data: fees } = useGetAllFeeQuery(user?.token);

  const totalPayable = fees?.reduce(
    (sum: any, fees: { payable: any }) => sum + fees.payable,
    0
  );
  const totalPaid = fees?.reduce(
    (sum: any, fee: { paid: any }) => sum + fee.paid,
    0
  );
  const totalPending = totalPayable - totalPaid;

  return (
    <div className="p-4 lg:p-6 bg-background text-foreground min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold">
            Welcome Back 👋 {user?.name || 'HR'}
          </h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <Link to={'/students'}>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">
                      Total Students
                    </p>
                    <p className="text-xl font-bold">{students?.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to={'/batch'}>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <UserCheck className="h-5 w-5 text-[#26BDB0]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Total Batch</p>
                    <p className="text-xl font-bold">{lengthOfBatches}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to={'/courses'}>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Building2 className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">
                      Total Course
                    </p>
                    <p className="text-xl font-bold">{lengthOfCourses}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to={'/enquiry'}>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">
                      Total Enquiry
                    </p>
                    <p className="text-xl font-bold">{lengthOfEnquiries}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-md lg:text-lg text-center">
                <h1>Total Student's Paid and Pending Fee</h1>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="lg:h-56 h-20 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Total Payable', value: totalPaid },
                        { name: 'Total Pending', value: totalPending },
                      ]}
                      innerRadius="50%"
                      outerRadius="80%"
                      paddingAngle={5}
                      dataKey="value"
                    >
                      <Cell fill="#127971" />
                      <Cell fill="#49ccc1e2" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-row justify-center items-center gap-4 lg:mt-4">
                  {/* Boys Label */}
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[#26BDB0] rounded-full" />

                    <span className="text-sm sm:text-base truncate max-w-[80px] sm:max-w-none">
                      Total Paid:{' '}
                      {((totalPaid / totalPayable) * 100).toFixed(2)}%
                    </span>
                  </div>
                  {/* Girls Label */}
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-teal-300 rounded-full" />

                    <span className="text-sm sm:text-base truncate max-w-[80px] sm:max-w-none">
                      Total Pending :{' '}
                      {((totalPending / totalPayable) * 100).toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <AttendanceBar />
        </div>
        {/* <FeePendingTable /> */}
        <FeePending />
      </div>
    </div>
  );
};

export default HrDashboard;
